/**
 * ATTEST-UI-1 screen 1 — engagement-type intake.
 *
 * These are the `engagement_router`'s own questions, asked in the order it asks
 * them. The answers decide whether the ask routes to AT-C 215 agreed-upon
 * procedures, to a SSARS factual confirmation, or to nothing at all — and the
 * router decides that, not this component. Nothing here pre-screens the ask or
 * hides a question based on a previous answer; a wizard that quietly steers the
 * user away from a refusal defeats the point of having one.
 *
 * Which questions get asked does follow the *declared* engagement basis, which
 * is not the same thing (ATTEST-CLASSIFY-BASIS-QUESTIONS-1). Both remaining
 * yes/no questions are AT-C 215 gates the router reads on no other route, so a
 * factual-confirmation basis asks neither and this step renders no question
 * block at all. `classifyQuestions.ts` owns that mapping and the reasoning.
 *
 * There used to be a third question — "needs work beyond your own records?" —
 * and it is gone as an *input* (ATTEST-BASIS-UNIFY-1). It was the basis
 * selector's own question asked a second time, in two-way form, far enough down
 * the page that the two answers could disagree; the letter would then carry one
 * engagement's scope sentence and the other's disclaimers. The basis is now the
 * single place that decision is made, and `route()` reads the engagement type
 * off it. What survives is the *answer*, rendered read-only inside the basis
 * detail — a hint that shows the CPA what they just chose, not a control that
 * lets them contradict it.
 *
 * The type list is data-driven off `GET /letter-types`, so a new letter type
 * that clears legal review appears here with no frontend change (brief §43).
 */
import type {
  AttestPolicy,
  AttestRefusal,
  EngagementBasisChoice,
  LetterTypeSummary,
  RoutingDecision,
} from '@/components/attest/api'
import { ClassifyQuestionGrid } from '@/components/attest/ClassifyQuestionGrid'
import { ClassifyResult } from '@/components/attest/ClassifyResult'
import {
  answerPatch,
  clearUnaskedAnswers,
  questionsFor,
  type ClassifyBooleanField,
  type ClassifyFormState,
} from '@/components/attest/classifyQuestions'
import { EngagementBasisPicker } from '@/components/attest/EngagementBasisPicker'
import type { AttestStepKey } from '@/components/attest/refusalGuidance'
import {
  replaceRequestLine,
  RequestLineCoach,
} from '@/components/attest/RequestLineCoach'
import {
  EXAMPLE_PLACEHOLDER,
  RequiredFieldsNote,
  RequiredMark,
  type RequiredFieldEntry,
} from '@/components/attest/requiredFields'
import { StepContinue } from '@/components/attest/StepGate'
import { HelpDot } from '@/components/attest/wizardHelp'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

// The form shape moved to `classifyQuestions` alongside the question set it
// feeds. Re-exported so callers keep importing it from the step they render.
export { EMPTY_CLASSIFY_FORM } from '@/components/attest/classifyQuestions'
export type { ClassifyFormState } from '@/components/attest/classifyQuestions'

export interface ClassifyStepProps {
  letterTypes: LetterTypeSummary[]
  selectedTypeId: string
  onSelectType: (id: string) => void
  /** The chosen type's `engagement_basis`, or null when it hard-wires one. */
  basisChoice: EngagementBasisChoice | null
  selectedBasis: string
  onSelectBasis: (key: string) => void
  policy: AttestPolicy | null
  form: ClassifyFormState
  onFormChange: (patch: Partial<ClassifyFormState>) => void
  decision: RoutingDecision | null
  refusal: AttestRefusal | null
  busy: boolean
  onClassify: () => void
  onContinue: () => void
  /** This step's still-empty required fields — gates Continue (UX-2 D3). */
  missingRequired?: RequiredFieldEntry[]
  /** Land on one of them without leaving the step (UX-2 D2). */
  onCorrect?: (fieldId: string) => void
  /**
   * Leave this step for the one a refusal is attributable to (Bundle 2 item 3).
   *
   * Distinct from `onCorrect`, which only moves focus within the current step:
   * a `/classify` refusal is not always about a step-1 field, so the panel this
   * step escalates to needs the navigation that can actually change steps.
   */
  onGoToStep?: (step: AttestStepKey, fieldAnchor?: string) => void
  /**
   * Fill the whole wizard with the worked example, or undefined to not offer it
   * (ATTEST-UX-AND-DEMO-READINESS-1 item 2).
   *
   * Optional because the page above withholds it once anything has been typed:
   * loading the example overwrites all four forms and there is no undo, so the
   * button removes itself rather than asking a CPA mid-letter to confirm they
   * did not mean it. It is a prefill and nothing more — the request still goes
   * to `/classify`, and every gate runs on it exactly as it would on typed
   * input.
   */
  onLoadDemo?: () => void
}

export function ClassifyStep({
  letterTypes, selectedTypeId, onSelectType, basisChoice, selectedBasis,
  onSelectBasis, policy, form, onFormChange,
  decision, refusal, busy, onClassify, onContinue,
  missingRequired = [], onCorrect, onGoToStep, onLoadDemo,
}: ClassifyStepProps) {
  const assuranceLevels = Object.keys(policy?.assurance_levels ?? {})
  const routed = decision && !decision.refused
  const basisKey = selectedBasis || basisChoice?.default || ''
  const basis = basisChoice?.options.find(o => o.key === basisKey) ?? null

  /**
   * The engagement type the request would be issued under as the form stands.
   *
   * Keyed off `engagement_type`, never off the basis key: the keys are values a
   * letter type picks for itself in its own YAML, while `engagement_type` is a
   * policy constant the router compares against. A UI switched on key strings
   * would silently mis-handle the next letter type that names its bases
   * differently.
   */
  const engagementTypeFor = (typeId: string, basisChoiceKey: string): string | null => {
    const type = letterTypes.find(t => t.type_id === typeId)
    if (!type) return null
    const choice = type.engagement_basis
    if (!choice) return type.engagement_type
    const option = choice.options.find(o => o.key === (basisChoiceKey || choice.default))
    return option?.engagement_type ?? type.engagement_type
  }

  const questions = questionsFor(basis?.engagement_type ?? engagementTypeFor(selectedTypeId, basisKey))

  /** Drop any answer the incoming question set will not ask for. */
  const dropUnasked = (engagementType: string | null) => {
    const patch = clearUnaskedAnswers(questionsFor(engagementType), form)
    if (Object.keys(patch).length > 0) onFormChange(patch)
  }

  return (
    <div className="space-y-5">
      <RequiredFieldsNote />

      <div>
        {/* `block`, not the shared Label's flex — see `RequiredMark`: a flex
            label pushes the star to the right edge once the text fills the row. */}
        {/* The "(i)" sits beside the label, never inside it: a `<button>` inside
            a `<label>` is a second activation target on the same click. */}
        <div className="flex items-center gap-1">
          <Label className="block" htmlFor="attest-letter-type">
            Letter type
            <RequiredMark field="attest-letter-type" />
          </Label>
          <HelpDot field="letterType" />
        </div>
        <select
          id="attest-letter-type"
          data-testid="letter-type-select"
          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          value={selectedTypeId}
          onChange={e => {
            // Changing type drops the basis (`LetterGenerator`), so the incoming
            // set is the new type's default basis, not the current key.
            onSelectType(e.target.value)
            dropUnasked(engagementTypeFor(e.target.value, ''))
          }}
        >
          <option value="">Select a letter type…</option>
          {letterTypes.map(t => (
            <option key={t.type_id} value={t.type_id}>
              {t.display_name}
              {t.status !== 'live' ? ' (draft)' : ''}
            </option>
          ))}
        </select>
        {letterTypes.length === 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            No letter types are available in this deployment.
          </p>
        )}
      </div>

      {basisChoice && (
        <EngagementBasisPicker
          choice={basisChoice}
          basisKey={basisKey}
          onSelectBasis={key => {
            onSelectBasis(key)
            dropUnasked(engagementTypeFor(selectedTypeId, key))
          }}
        />
      )}

      <div>
        <div className="flex items-center gap-1">
          <Label className="block" htmlFor="requested-items">
            What is the requester asking you to confirm?
            <RequiredMark field="requested-items" />
          </Label>
          <HelpDot field="requestedItems" />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          One item per line, in the requester&rsquo;s own words. Paste the lender&rsquo;s
          wording rather than paraphrasing it — the deny-list scan reads what was
          actually asked.
        </p>
        {/* The examples are prefixed and italicised because they were being read
            as entered content — a CPA seeing two greyed asks here and believing
            the box was filled is how the NO_REQUEST_ITEMS refusal surprised
            people (ATTEST-UX-1 D1). */}
        <Textarea
          id="requested-items"
          data-testid="requested-items"
          rows={5}
          className={`mt-2 ${EXAMPLE_PLACEHOLDER}`}
          value={form.requestedItemsText}
          onChange={e => onFormChange({ requestedItemsText: e.target.value })}
          placeholder={'e.g. Confirm the business has been operating since 2019\ne.g. Confirm gross receipts reported on the 2024 return'}
        />
      </div>

      {/* Renders nothing at all on the factual basis — the empty-set guard lives
          in the grid itself (ATTEST-BASIS-UNIFY-1). */}
      <ClassifyQuestionGrid
        questions={questions}
        engagementType={basis?.engagement_type ?? engagementTypeFor(selectedTypeId, basisKey) ?? ''}
        answerOf={(field: ClassifyBooleanField) => form[field]}
        onAnswer={(field: ClassifyBooleanField, v: boolean) => onFormChange(answerPatch(field, v))}
      />


      <div className="max-w-sm">
        <div className="flex items-center gap-1">
          <Label htmlFor="assurance-level">Assurance the requester asked for (optional)</Label>
          <HelpDot field="assuranceLevel" />
        </div>
        <select
          id="assurance-level"
          data-testid="assurance-level"
          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          value={form.requestedAssuranceLevel}
          onChange={e => onFormChange({ requestedAssuranceLevel: e.target.value })}
        >
          <option value="">Not stated</option>
          {assuranceLevels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onClassify} disabled={busy} data-testid="classify-submit">
            {busy ? 'Classifying…' : 'Classify this request'}
          </Button>
          {onLoadDemo && (
            <Button
              variant="ghost"
              onClick={onLoadDemo}
              disabled={busy}
              data-testid="classify-load-demo"
            >
              Fill in a worked example
            </Button>
          )}
        </div>
        {onLoadDemo && (
          <p className="text-xs text-muted-foreground" data-testid="classify-demo-note">
            A real lender request, already filled in — a commercial facility
            confirmation a CPA may sign. Nothing is skipped: every gate still runs
            on it. Offered only while this form is empty.
          </p>
        )}
        {routed && (
          <StepContinue
            missing={missingRequired}
            onContinue={onContinue}
            onCorrect={onCorrect}
            testId="classify-continue"
            label="Continue to the letter"
            variant="outline"
          />
        )}
      </div>

      {/* Not `RefusalPanel` directly any more (ATTEST-REQUEST-COACH-1). A denied
          line that has a permitted equivalent is coached against the line
          first; the panel is what the coach escalates to once there is nothing
          left to suggest or the CPA has turned every suggestion down. */}
      {refusal && (
        <RequestLineCoach
          refusal={refusal}
          decision={decision}
          requestedItemsText={form.requestedItemsText}
          onUseWording={(original, replacement) =>
            onFormChange({
              requestedItemsText: replaceRequestLine(
                form.requestedItemsText, original, replacement,
              ),
            })}
          onReclassify={onClassify}
          onGoToStep={onGoToStep}
        />
      )}

      {/* Renders on both paths — see `ClassifyResult`. A refusal that shows only
          the banner tells the CPA "no" and nothing they can act on. */}
      {decision && <ClassifyResult decision={decision} policy={policy} />}
    </div>
  )
}
